import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  MenuItem,
  IconButton,
  Tabs,
  Tab,
  Menu,
  useMediaQuery,
  Tooltip,
  Typography,
  Divider,
  Avatar,
  Badge,
  ListItemIcon,
  ListItemText,
  Paper,
  Chip,
  alpha,
  Stack,
  Switch,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import LogoutIcon from "@mui/icons-material/Logout";
import ReceiptIcon from "@mui/icons-material/Receipt";
import EventIcon from "@mui/icons-material/Event";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PeopleIcon from "@mui/icons-material/People";
import SettingsIcon from "@mui/icons-material/Settings";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import PersonIcon from "@mui/icons-material/Person";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import HomeIcon from "@mui/icons-material/Home";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import TimerIcon from "@mui/icons-material/Timer";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import LanguageIcon from "@mui/icons-material/Language";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import DashboardIcon from "@mui/icons-material/Dashboard";
import MenuIcon from "@mui/icons-material/Menu";
import VerticalAlignBottomIcon from "@mui/icons-material/VerticalAlignBottom";
import MultipleStopIcon from "@mui/icons-material/MultipleStop";
import AlignHorizontalLeftIcon from "@mui/icons-material/AlignHorizontalLeft";
import AlignHorizontalRightIcon from "@mui/icons-material/AlignHorizontalRight";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useWorkStatus } from "../../contexts/WorkStatusContext";
import { useBadgeRefresh } from "../../contexts/BadgeRefreshContext";
import { useDock } from "../../contexts/DockContext";
import { useDrawer } from "../../contexts/DrawerContext";
import { useDisplayMode } from "../../contexts/DisplayModeContext";
import {
  canAccessLeaveManagement,
  canAccessTimeTracking,
  canManageUsers,
} from "../../services/api";
import userPreferenceApi from "../../services/user-preference-api";
import reimbursementV2Api from "../../services/reimbursement-v2-api";
import invoiceV2Api from "../../services/invoice-v2-api";
import leaveApiService from "../../services/leave-api";
import { canApproveLeave } from "../../services/api";

// Language flag mapping
const LANGUAGE_FLAGS: Record<string, { flag: string; name: string }> = {
  en: { flag: "🇺🇸", name: "English" },
  ja: { flag: "🇯🇵", name: "日本語" },
  fil: { flag: "🇵🇭", name: "Filipino" },
  vi: { flag: "🇻🇳", name: "Tiếng Việt" },
};

// Mini logo as base64 image
const MINI_LOGO =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABwAAAAcCAMAAABF0y+mAAAAclBMVEX////+9/f/+/v3wsD50dD61tT97e3yoZ7//Pz629r74N/teHL74+Lugn398vLpVk/3ysjrZmD2vbvmNiz0tbLqXlfzrKnlIxTnRDzjAADypqPkHg786OfviYXoTETnQDjkFwDmOjDlLCDqYVvrbmnxmZaTHOF8AAAAmElEQVR4AdzLxWEDARDAQB0zM3P/JSZm3Ao8b4kfoHCiKghUjRPdQGBaNqiWg8D1VPCDEIER2RAnNoI0CyEvkJSVC1aNJG9isE7zg+v4XKVNC2XXv+WVASdxl4B2ml8N1agCbjWFGLPFu6LyIFwqnX5N+NAvXclWDVBnJp/iddnX2sabc74dc9XotE2EJE1jlDzv/zeE0xkAgDkH9k/cmnwAAAAASUVORK5CYII=";

const LogoBrand = () => {
  const isMobile = useMediaQuery("(max-width:1000px)");

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: isMobile ? 1 : 2,
      }}
    >
      {/* Logo Image */}
      <Box
        component="img"
        src={MINI_LOGO}
        alt="Nandemo Logo"
        sx={{
          width: isMobile ? 32 : 40,
          height: isMobile ? 32 : 40,
          objectFit: "contain",
          borderRadius: "50%",
        }}
      />

      {/* App Name and Company */}
      {!isMobile && (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "1.25rem",
              lineHeight: 1,
              mb: 0.25,
            }}
          >
            Nandemo
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.75rem",
              opacity: 0.7,
              lineHeight: 1,
            }}
          >
            Excelfore Japan
          </Typography>
        </Box>
      )}
    </Box>
  );
};

interface HeaderProps {
  onLogout: () => void;
}

const Header: React.FC<HeaderProps> = ({ onLogout }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const { workStatus } = useWorkStatus();
  const { refreshTrigger } = useBadgeRefresh();
  const { dockPosition, setDockPosition } = useDock();
  const { drawerPosition, setDrawerPosition } = useDrawer();
  const { displayMode, setDisplayMode } = useDisplayMode();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:1000px)");
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const [settingsMenuAnchor, setSettingsMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [languageMenuAnchor, setLanguageMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [themeMenuAnchor, setThemeMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [linksMenuAnchor, setLinksMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [dockPositionMenuAnchor, setDockPositionMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [drawerPositionMenuAnchor, setDrawerPositionMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [pendingReimbursementCount, setPendingReimbursementCount] =
    useState<number>(0);
  const [pendingLeaveCount, setPendingLeaveCount] = useState<number>(0);
  const [financeDeskAttentionCount, setFinanceDeskAttentionCount] =
    useState<number>(0);

  const hasLeaveAccess = canAccessLeaveManagement(user || null);
  const hasTimeTrackingAccess = canAccessTimeTracking(user || null);
  const hasUserManagementAccess = canManageUsers(user || null);
  const hasLeaveApprovalAccess = canApproveLeave(user || null);

  // Fetch pending reimbursement and invoice counts for admin users
  useEffect(() => {
    if (!hasUserManagementAccess) {
      return;
    }

    const fetchPendingCount = async () => {
      try {
        const [reimbursementCount, invoiceCount] = await Promise.all([
          reimbursementV2Api.getPendingRequestsCount(),
          invoiceV2Api.getPendingRequestsCount(),
        ]);
        setPendingReimbursementCount(reimbursementCount + invoiceCount);
      } catch (error) {
        console.error("Failed to fetch pending counts:", error);
      }
    };

    // Fetch immediately
    fetchPendingCount();

    // Refresh every 30 seconds
    const interval = setInterval(fetchPendingCount, 30000);

    return () => clearInterval(interval);
  }, [hasUserManagementAccess, refreshTrigger]);

  // Fetch pending leave requests count for approvers
  useEffect(() => {
    if (!hasLeaveApprovalAccess) {
      return;
    }

    const fetchPendingLeaveCount = async () => {
      try {
        const count = await leaveApiService.getPendingRequestsCount();
        setPendingLeaveCount(count);
      } catch (error) {
        console.error("Failed to fetch pending leave count:", error);
      }
    };

    // Fetch immediately
    fetchPendingLeaveCount();

    // Refresh every 30 seconds
    const interval = setInterval(fetchPendingLeaveCount, 30000);

    return () => clearInterval(interval);
  }, [hasLeaveApprovalAccess, refreshTrigger]);

  // Fetch attention count for Finance Desk (REJECTED or PROCESSED items)
  useEffect(() => {
    if (!user?.id) {
      return;
    }

    const fetchAttentionCount = async () => {
      try {
        const [reimbursementCount, invoiceCount] = await Promise.all([
          reimbursementV2Api.getAttentionRequestsCount(user.id),
          invoiceV2Api.getAttentionRequestsCount(user.id),
        ]);
        setFinanceDeskAttentionCount(reimbursementCount + invoiceCount);
      } catch (error) {
        console.error("Failed to fetch finance desk attention count:", error);
      }
    };

    // Fetch immediately
    fetchAttentionCount();

    // Refresh every 30 seconds
    const interval = setInterval(fetchAttentionCount, 30000);

    return () => clearInterval(interval);
  }, [user?.id, refreshTrigger]);

  // Get display name and email from workStatus if available, fallback to user
  const displayName =
    workStatus?.employeeName || user?.username || t("common.unknownUser");
  const displayEmail = user?.username ? `${user.username}@excelfore.com` : "";

  // Get initials for avatar
  const getInitials = (mobileView = false) => {
    if (workStatus?.employeeName) {
      const names = workStatus.employeeName.split(" ");
      if (names.length >= 2) {
        // For mobile, return only first letter
        if (mobileView) {
          return names[0][0].toUpperCase();
        }
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
      }
      // For mobile, return only first letter
      if (mobileView) {
        return workStatus.employeeName[0]?.toUpperCase() || "?";
      }
      return workStatus.employeeName.substring(0, 2).toUpperCase();
    }
    if (user?.username) {
      // For mobile, return only first letter
      if (mobileView) {
        return user.username[0]?.toUpperCase() || "?";
      }
      return user.username.substring(0, 2).toUpperCase();
    }
    return "??";
  };

  const handleLanguageChange = (language: string) => {
    i18n.changeLanguage(language);
    localStorage.setItem("language", language);
    setLanguageMenuAnchor(null);

    // Save to backend
    if (user?.id) {
      userPreferenceApi.updateLanguage(language).catch((err) => {
        console.error(t("error.failedToSaveLanguagePreference"), err);
      });
    }
  };

  const handleThemeToggle = () => {
    toggleTheme();

    // Calculate new theme mode
    const newThemeMode = isDarkMode ? "light" : "dark";

    // Save to localStorage immediately
    localStorage.setItem("themeMode", newThemeMode);

    // Save to backend
    if (user?.id) {
      userPreferenceApi.updateTheme(newThemeMode).catch((err) => {
        console.error(t("error.failedToSaveThemePreference"), err);
      });
    }
  };

  const handleSettingsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setSettingsMenuAnchor(event.currentTarget);
  };

  const handleSettingsMenuClose = () => {
    setSettingsMenuAnchor(null);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleNavigateToTimeTracking = () => {
    setUserMenuAnchor(null);
    navigate("/timetracking");
  };

  const handleLogout = () => {
    setUserMenuAnchor(null);
    onLogout();
  };

  const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setLanguageMenuAnchor(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageMenuAnchor(null);
  };

  const handleThemeMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setThemeMenuAnchor(event.currentTarget);
  };

  const handleThemeMenuClose = () => {
    setThemeMenuAnchor(null);
  };

  const handleLinksMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setLinksMenuAnchor(event.currentTarget);
  };

  const handleLinksMenuClose = () => {
    setLinksMenuAnchor(null);
  };

  const handleDockPositionMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setDockPositionMenuAnchor(event.currentTarget);
  };

  const handleDockPositionMenuClose = () => {
    setDockPositionMenuAnchor(null);
  };

  const handleDrawerPositionMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
  ) => {
    event.stopPropagation();
    setDrawerPositionMenuAnchor(event.currentTarget);
  };

  const handleDrawerPositionMenuClose = () => {
    setDrawerPositionMenuAnchor(null);
  };

  // Build visible tabs array based on permissions
  // In mobile view, limit to Home, Time Tracking, Leave, Finance Desk, and My Profile only
  const visibleTabs = isMobile
    ? [
        { path: "/home", label: "home" },
        ...(hasTimeTrackingAccess
          ? [{ path: "/timetracking", label: "timetracking" }]
          : []),
        ...(hasLeaveAccess ? [{ path: "/leave", label: "leave" }] : []),
        // { path: "/reservations", label: "reservations" },
        { path: "/finance-desk", label: "finance.desk" },
        { path: "/profile", label: "profile" },
      ]
    : [
        { path: "/home", label: "home" },
        ...(hasTimeTrackingAccess
          ? [{ path: "/timetracking", label: "timetracking" }]
          : []),
        ...(hasLeaveAccess ? [{ path: "/leave", label: "leave" }] : []),
        // { path: "/reservations", label: "reservations" },
        { path: "/finance-desk", label: "finance.desk" },
        ...(hasUserManagementAccess
          ? [
              {
                path: "/finance-admin-desk",
                label: "finance.admin.desk",
              },
            ]
          : []),
        ...(hasUserManagementAccess
          ? [{ path: "/users", label: "users" }]
          : []),
        ...(hasUserManagementAccess
          ? [{ path: "/configurations", label: "configurations" }]
          : []),
        { path: "/profile", label: "profile" },
      ];

  const getTabValue = () => {
    // For exact path matching to avoid /reimbursements-v2-processing matching /reimbursements
    if (location.pathname === "/reimbursements-v2-processing") {
      return visibleTabs.findIndex(
        (tab) => tab.path === "/reimbursements-v2-processing",
      );
    }

    const currentIndex = visibleTabs.findIndex((tab) =>
      location.pathname.startsWith(tab.path),
    );
    return currentIndex >= 0 ? currentIndex : 0;
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    if (newValue >= 0 && newValue < visibleTabs.length) {
      navigate(visibleTabs[newValue].path);
    }
  };

  return (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: (theme) => theme.palette.background.paper,
        color: (theme) => theme.palette.text.primary,
        boxShadow: (theme) =>
          isDarkMode
            ? "0 2px 8px rgba(0,0,0,0.3)"
            : "0 2px 4px rgba(0,0,0,0.1)",
        zIndex: (theme) => theme.zIndex.appBar,
        borderBottom: (theme) =>
          `1px solid ${isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
      }}
    >
      <Toolbar
        sx={{ minHeight: "64px !important", justifyContent: "space-between" }}
      >
        {/* Left box - Logo and Brand */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: isMobile ? "auto" : 280,
            flex: isMobile ? "0 0 auto" : "0 0 280px",
          }}
        >
          <LogoBrand />
        </Box>

        {/* Middle box - Tabs */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            flex: "1 1 auto",
            mx: isMobile ? 1 : 0,
          }}
        >
          <Tabs
            value={getTabValue()}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTabs-flexContainer": {
                gap: 0.5,
              },
              "& .MuiTab-root": {
                color: (theme) => theme.palette.text.secondary,
                opacity: 0.6,
                minHeight: "64px !important",
                transition: "all 0.3s ease",
                position: "relative",
                borderRadius: "8px 8px 0 0",
                px: 2,
                py: 1,
                "&:hover": {
                  opacity: 0.85,
                  backgroundColor: (theme) =>
                    isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
                },
                ...(isMobile && {
                  minWidth: "auto",
                  px: isSmallScreen ? 1.5 : 1.5,
                  "& .MuiTab-iconWrapper": {
                    marginRight: isSmallScreen ? 0 : 0.5,
                  },
                }),
              },
              "& .Mui-selected": {
                color: (theme) => theme.palette.primary.main,
                opacity: 1,
                backgroundColor: (theme) =>
                  isDarkMode
                    ? "rgba(33, 150, 243, 0.3)"
                    : "rgba(33, 150, 243, 0.2)",
                fontWeight: 700,
              },
              "& .MuiTabs-indicator": {
                backgroundColor: (theme) => theme.palette.primary.main,
                height: "4px",
              },
            }}
            indicatorColor="primary"
          >
            {visibleTabs.map((tab, index) => {
              const getIcon = () => {
                const baseIcon = (() => {
                  switch (tab.label) {
                    case "home":
                      return <HomeIcon />;
                    case "timetracking":
                      return <WorkHistoryIcon />;
                    case "leave":
                      return <EventIcon />;
                    case "reservations":
                      return <EventAvailableIcon />;
                    case "calendar":
                      return <CalendarMonthIcon />;
                    case "finance.desk":
                      return <ReceiptIcon />;
                    case "finance.admin.desk":
                      return <ReceiptIcon />;
                    case "users":
                      return <PeopleIcon />;
                    case "configurations":
                      return <SettingsIcon />;
                    case "profile":
                      return <PersonIcon />;
                    default:
                      return <HomeIcon />;
                  }
                })();

                // Add badge for Finance Desk if there are items needing attention
                if (
                  tab.label === "finance.desk" &&
                  financeDeskAttentionCount > 0
                ) {
                  return (
                    <Badge
                      badgeContent={financeDeskAttentionCount}
                      color="error"
                      max={99}
                    >
                      {baseIcon}
                    </Badge>
                  );
                }

                // Add badge for Finance Admin Desk if there are pending requests
                if (
                  tab.label === "finance.admin.desk" &&
                  pendingReimbursementCount > 0
                ) {
                  return (
                    <Badge
                      badgeContent={pendingReimbursementCount}
                      color="error"
                      max={99}
                    >
                      {baseIcon}
                    </Badge>
                  );
                }

                // Add badge for Leave if there are pending leave requests
                if (tab.label === "leave" && pendingLeaveCount > 0) {
                  return (
                    <Badge
                      badgeContent={pendingLeaveCount}
                      color="error"
                      max={99}
                    >
                      {baseIcon}
                    </Badge>
                  );
                }

                return baseIcon;
              };

              return (
                <Tooltip
                  key={tab.path}
                  title={isSmallScreen ? t(tab.label + ".tab-title") : ""}
                >
                  <Tab
                    value={index}
                    icon={getIcon()}
                    iconPosition="top"
                    label={!isSmallScreen && t(tab.label + ".tab-title")}
                  />
                </Tooltip>
              );
            })}
          </Tabs>
        </Box>

        {/* Right box - Theme Toggle, Language and Logout */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: isMobile ? 0.5 : 1,
            width: isMobile ? "auto" : 280,
            flex: isMobile ? "0 0 auto" : "0 0 280px",
          }}
        >
          {isMobile ? (
            <>
              {/* Settings Icon for Mobile */}
              <Tooltip title={t("common.settings")}>
                <IconButton
                  onClick={handleSettingsMenuOpen}
                  size="small"
                  sx={{
                    color: (theme) => theme.palette.text.primary,
                  }}
                >
                  <SettingsIcon />
                </IconButton>
              </Tooltip>

              {/* User Avatar for Mobile */}
              <IconButton
                onClick={handleUserMenuOpen}
                size="small"
                sx={{ p: 0, ml: 0.5 }}
              >
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  variant="dot"
                  sx={{
                    "& .MuiBadge-badge": {
                      backgroundColor:
                        workStatus?.currentStatus === "IN"
                          ? workStatus?.isOnBreak
                            ? "#2196f3" // Blue for on break
                            : "#44b700" // Green for working
                          : "#9e9e9e", // Grey for not working
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: "primary.main",
                      fontSize: "0.875rem",
                      fontWeight: 600,
                    }}
                  >
                    {getInitials(true)}
                  </Avatar>
                </Badge>
              </IconButton>

              {/* Settings Menu (shared with desktop) */}
              <Menu
                anchorEl={settingsMenuAnchor}
                open={Boolean(settingsMenuAnchor)}
                onClose={handleSettingsMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 260,
                    borderRadius: 2,
                  },
                }}
              >
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      fontSize: "0.75rem",
                    }}
                  >
                    {t("common.settings")}
                  </Typography>
                </Box>
                <Divider />

                {/* Theme Toggle */}
                <MenuItem sx={{ py: 1.5, px: 2 }}>
                  <ListItemIcon>
                    {isDarkMode ? (
                      <Brightness7Icon fontSize="small" />
                    ) : (
                      <Brightness4Icon fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText primary={t("common.darkMode")} />
                  <Switch
                    checked={isDarkMode}
                    onChange={handleThemeToggle}
                    size="small"
                  />
                </MenuItem>

                <Divider sx={{ my: 0.5 }} />

                {/* Language */}
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                    }}
                  >
                    {t("common.language")}
                  </Typography>
                </Box>
                {Object.entries(LANGUAGE_FLAGS).map(
                  ([code, { flag, name }]) => (
                    <MenuItem
                      key={code}
                      onClick={() => handleLanguageChange(code)}
                      selected={i18n.language === code}
                      sx={{ py: 1.5, px: 2 }}
                    >
                      <Typography sx={{ fontSize: "1.5rem", mr: 1.5 }}>
                        {flag}
                      </Typography>
                      <Typography sx={{ flex: 1 }}>{name}</Typography>
                      {i18n.language === code && (
                        <CheckCircleIcon
                          fontSize="small"
                          sx={{ color: "primary.main" }}
                        />
                      )}
                    </MenuItem>
                  ),
                )}
              </Menu>

              {/* User Menu (shared with desktop) */}
              <Menu
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={handleUserMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 280,
                    borderRadius: 2,
                  },
                }}
              >
                {/* User Info */}
                <Box sx={{ px: 2, py: 2 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: "primary.main",
                        fontSize: "1rem",
                      }}
                    >
                      {getInitials()}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {displayName}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {displayEmail}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                <Divider />

                {/* Work Status */}
                {hasTimeTrackingAccess && workStatus && (
                  <>
                    <Box sx={{ px: 2, py: 1.5 }}>
                      <Stack spacing={1}>
                        <Chip
                          icon={
                            workStatus.currentStatus === "IN" ? (
                              workStatus.isOnBreak ? (
                                <PauseCircleIcon />
                              ) : (
                                <CheckCircleIcon />
                              )
                            ) : (
                              <CancelIcon />
                            )
                          }
                          label={
                            workStatus.currentStatus === "IN"
                              ? workStatus.isOnBreak
                                ? t("timetracking.status.ON_BREAK")
                                : t("timetracking.working")
                              : t("timetracking.notWorking")
                          }
                          size="small"
                          color={
                            workStatus.currentStatus === "IN"
                              ? workStatus.isOnBreak
                                ? "info"
                                : "success"
                              : "default"
                          }
                          sx={{ width: "fit-content" }}
                        />
                        {workStatus.currentStatus === "IN" && (
                          <Typography variant="body2">
                            <TimerIcon
                              fontSize="small"
                              sx={{ verticalAlign: "middle", mr: 0.5 }}
                            />
                            {workStatus.currentSessionDuration}
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                    <MenuItem onClick={handleNavigateToTimeTracking}>
                      <ListItemIcon>
                        <AccessTimeIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={t("timetracking.goToTimeTracking")}
                      />
                    </MenuItem>
                    <Divider />
                  </>
                )}

                {/* Logout */}
                <MenuItem
                  onClick={handleLogout}
                  sx={{
                    py: 1.5,
                    px: 2,
                    color: "error.main",
                  }}
                >
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" sx={{ color: "error.main" }} />
                  </ListItemIcon>
                  <ListItemText primary={t("common.logout")} />
                </MenuItem>
              </Menu>
            </>
          ) : (
            <>
              {/* Settings Menu */}
              <Tooltip title={t("common.settings")}>
                <IconButton
                  onClick={handleSettingsMenuOpen}
                  size="small"
                  sx={{
                    color: (theme) => theme.palette.text.primary,
                    transition: "all 0.3s ease",
                    borderRadius: "50%",
                    "&:hover": {
                      backgroundColor: (theme) =>
                        isDarkMode
                          ? "rgba(255,255,255,0.15)"
                          : "rgba(0,0,0,0.08)",
                      transform: "rotate(45deg) scale(1.1)",
                    },
                  }}
                >
                  <SettingsIcon />
                </IconButton>
              </Tooltip>

              {/* Settings Dropdown Menu */}
              <Menu
                anchorEl={settingsMenuAnchor}
                open={Boolean(settingsMenuAnchor)}
                onClose={handleSettingsMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    minWidth: 300,
                    borderRadius: "12px",
                    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
                    border: "1px solid rgba(0, 0, 0, 0.06)",
                  },
                }}
                MenuListProps={{
                  sx: {
                    py: 0,
                  },
                }}
              >
                {/* Menu Header */}
                <Box
                  sx={{
                    px: 3,
                    py: 2,
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                    borderRadius: "12px 12px 0 0",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                    <SettingsIcon sx={{ fontSize: "1.2rem", mr: 1 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {t("common.settingsMenu.title")}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.9,
                      fontSize: "0.8rem",
                      lineHeight: 1.3,
                    }}
                  >
                    {t("common.settingsMenu.description")}
                  </Typography>
                </Box>

                <Divider />

                {/* Appearance Section */}
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      px: 1,
                    }}
                  >
                    {t("common.settingsMenu.appearance")}
                  </Typography>
                </Box>

                {/* Language Selector */}
                <MenuItem
                  onClick={handleLanguageMenuOpen}
                  sx={{
                    mx: 1,
                    borderRadius: "8px",
                    minHeight: 48,
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <LanguageIcon sx={{ fontSize: "1.1rem" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("common.language")}
                    secondary={`${LANGUAGE_FLAGS[i18n.language]?.flag || ""} ${LANGUAGE_FLAGS[i18n.language]?.name || ""}`}
                    primaryTypographyProps={{
                      fontSize: "0.9rem",
                      fontWeight: 500,
                    }}
                    secondaryTypographyProps={{
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      transform: languageMenuAnchor
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "transform 0.2s",
                    }}
                  >
                    <KeyboardArrowDownIcon
                      sx={{ fontSize: "1rem", color: "action.active" }}
                    />
                  </Box>
                </MenuItem>

                {/* Theme Selector */}
                <MenuItem
                  onClick={handleThemeMenuOpen}
                  sx={{
                    mx: 1,
                    borderRadius: "8px",
                    minHeight: 48,
                    mb: 0.5,
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {isDarkMode ? (
                      <Brightness4Icon sx={{ fontSize: "1.1rem" }} />
                    ) : (
                      <Brightness7Icon sx={{ fontSize: "1.1rem" }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={t("common.theme")}
                    secondary={t(isDarkMode ? "common.dark" : "common.light")}
                    primaryTypographyProps={{
                      fontSize: "0.9rem",
                      fontWeight: 500,
                    }}
                    secondaryTypographyProps={{
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      transform: themeMenuAnchor
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "transform 0.2s",
                    }}
                  >
                    <KeyboardArrowDownIcon
                      sx={{ fontSize: "1rem", color: "action.active" }}
                    />
                  </Box>
                </MenuItem>

                <Divider sx={{ mx: 2 }} />

                {/* Interface Section - Hide on mobile */}
                {!isMobile && (
                  <>
                    <Box sx={{ px: 2, py: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          px: 1,
                        }}
                      >
                        {t("common.settingsMenu.interface")}
                      </Typography>
                    </Box>

                    {/* Display Mode Selector */}
                    <MenuItem
                      onClick={handleLinksMenuOpen}
                      sx={{
                        mx: 1,
                        borderRadius: "8px",
                        minHeight: 48,
                        mb: 0.5,
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {displayMode === "card" ? (
                          <ViewModuleIcon sx={{ fontSize: "1.1rem" }} />
                        ) : displayMode === "dock" ? (
                          <DashboardIcon sx={{ fontSize: "1.1rem" }} />
                        ) : (
                          <MenuIcon sx={{ fontSize: "1.1rem" }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={t("common.linksSettings")}
                        secondary={t(
                          displayMode === "card"
                            ? "common.cardMode"
                            : displayMode === "dock"
                              ? "common.dockMode"
                              : "common.drawerMode",
                        )}
                        primaryTypographyProps={{
                          fontSize: "0.9rem",
                          fontWeight: 500,
                        }}
                        secondaryTypographyProps={{
                          fontSize: "0.75rem",
                          color: "text.secondary",
                        }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          transform: linksMenuAnchor
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                          transition: "transform 0.2s",
                        }}
                      >
                        <KeyboardArrowDownIcon
                          sx={{ fontSize: "1rem", color: "action.active" }}
                        />
                      </Box>
                    </MenuItem>

                    {/* Dock Position Selector - Only show when in Dock Mode */}
                    {displayMode === "dock" && (
                      <MenuItem
                        onClick={handleDockPositionMenuOpen}
                        sx={{
                          mx: 1,
                          borderRadius: "8px",
                          minHeight: 48,
                          mb: 0.5,
                          "&:hover": {
                            backgroundColor: "action.hover",
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {dockPosition === "bottom" ? (
                            <VerticalAlignBottomIcon
                              sx={{ fontSize: "1.1rem" }}
                            />
                          ) : (
                            <MultipleStopIcon sx={{ fontSize: "1.1rem" }} />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={t("common.dockPosition")}
                          secondary={t(`common.${dockPosition}`)}
                          primaryTypographyProps={{
                            fontSize: "0.9rem",
                            fontWeight: 500,
                          }}
                          secondaryTypographyProps={{
                            fontSize: "0.75rem",
                            color: "text.secondary",
                          }}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            transform: dockPositionMenuAnchor
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                            transition: "transform 0.2s",
                          }}
                        >
                          <KeyboardArrowDownIcon
                            sx={{ fontSize: "1rem", color: "action.active" }}
                          />
                        </Box>
                      </MenuItem>
                    )}

                    {/* Drawer Position Selector - Only show when in Drawer Mode */}
                    {displayMode === "drawer" && (
                      <MenuItem
                        onClick={handleDrawerPositionMenuOpen}
                        sx={{
                          mx: 1,
                          borderRadius: "8px",
                          minHeight: 48,
                          mb: 0.5,
                          "&:hover": {
                            backgroundColor: "action.hover",
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {drawerPosition === "bottom" ? (
                            <VerticalAlignBottomIcon
                              sx={{ fontSize: "1.1rem" }}
                            />
                          ) : (
                            <MultipleStopIcon sx={{ fontSize: "1.1rem" }} />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={t("common.drawerPosition")}
                          secondary={t(`common.${drawerPosition}`)}
                          primaryTypographyProps={{
                            fontSize: "0.9rem",
                            fontWeight: 500,
                          }}
                          secondaryTypographyProps={{
                            fontSize: "0.75rem",
                            color: "text.secondary",
                          }}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            transform: drawerPositionMenuAnchor
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                            transition: "transform 0.2s",
                          }}
                        >
                          <KeyboardArrowDownIcon
                            sx={{ fontSize: "1rem", color: "action.active" }}
                          />
                        </Box>
                      </MenuItem>
                    )}
                  </>
                )}
              </Menu>

              {/* Language Submenu */}
              <Menu
                anchorEl={languageMenuAnchor}
                open={Boolean(languageMenuAnchor)}
                onClose={handleLanguageMenuClose}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                PaperProps={{
                  sx: {
                    minWidth: 300,
                    borderRadius: "8px",
                    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
                    border: "1px solid rgba(0, 0, 0, 0.06)",
                    zIndex: (theme) => theme.zIndex.modal + 1,
                  },
                }}
                MenuListProps={{
                  sx: {
                    py: 1,
                  },
                }}
              >
                {Object.entries(LANGUAGE_FLAGS).map(
                  ([code, { flag, name }]) => (
                    <MenuItem
                      key={code}
                      onClick={() => handleLanguageChange(code)}
                      selected={i18n.language === code}
                      sx={{
                        mx: 1,
                        borderRadius: "6px",
                        minHeight: 40,
                        "&.Mui-selected": {
                          backgroundColor: "primary.light",
                          color: "primary.contrastText",
                          "&:hover": {
                            backgroundColor: "primary.main",
                          },
                        },
                      }}
                    >
                      <ListItemText
                        primaryTypographyProps={{
                          fontSize: "0.875rem",
                          fontWeight: i18n.language === code ? 600 : 400,
                        }}
                      >
                        {flag} {name}
                      </ListItemText>
                    </MenuItem>
                  ),
                )}
              </Menu>

              {/* Theme Submenu */}
              <Menu
                anchorEl={themeMenuAnchor}
                open={Boolean(themeMenuAnchor)}
                onClose={handleThemeMenuClose}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                PaperProps={{
                  sx: {
                    minWidth: 300,
                    borderRadius: "8px",
                    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
                    border: "1px solid rgba(0, 0, 0, 0.06)",
                    zIndex: (theme) => theme.zIndex.modal + 1,
                  },
                }}
                MenuListProps={{
                  sx: {
                    py: 1,
                  },
                }}
              >
                {[
                  {
                    mode: "light",
                    icon: <Brightness7Icon />,
                    label: t("common.light"),
                  },
                  {
                    mode: "dark",
                    icon: <Brightness4Icon />,
                    label: t("common.dark"),
                  },
                ].map(({ mode, icon, label }) => (
                  <MenuItem
                    key={mode}
                    onClick={() => {
                      if (
                        (mode === "dark" && !isDarkMode) ||
                        (mode === "light" && isDarkMode)
                      ) {
                        handleThemeToggle();
                      }
                      handleThemeMenuClose();
                    }}
                    selected={
                      (mode === "dark" && isDarkMode) ||
                      (mode === "light" && !isDarkMode)
                    }
                    sx={{
                      mx: 1,
                      borderRadius: "6px",
                      minHeight: 40,
                      "&.Mui-selected": {
                        backgroundColor: "primary.light",
                        color: "primary.contrastText",
                        "&:hover": {
                          backgroundColor: "primary.main",
                        },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {React.cloneElement(icon, {
                        sx: {
                          fontSize: "1rem",
                          color:
                            (mode === "dark" && isDarkMode) ||
                            (mode === "light" && !isDarkMode)
                              ? "primary.contrastText"
                              : "inherit",
                        },
                      })}
                    </ListItemIcon>
                    <ListItemText
                      primaryTypographyProps={{
                        fontSize: "0.875rem",
                        fontWeight:
                          (mode === "dark" && isDarkMode) ||
                          (mode === "light" && !isDarkMode)
                            ? 600
                            : 400,
                      }}
                    >
                      {label}
                    </ListItemText>
                  </MenuItem>
                ))}
              </Menu>

              {/* Links Display Mode Submenu */}
              <Menu
                anchorEl={linksMenuAnchor}
                open={Boolean(linksMenuAnchor)}
                onClose={handleLinksMenuClose}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                PaperProps={{
                  sx: {
                    minWidth: 300,
                    borderRadius: "8px",
                    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
                    border: "1px solid rgba(0, 0, 0, 0.06)",
                    zIndex: (theme) => theme.zIndex.modal + 1,
                  },
                }}
                MenuListProps={{
                  sx: {
                    py: 1,
                  },
                }}
              >
                {[
                  {
                    value: "card",
                    label: t("common.cardMode"),
                    icon: <ViewModuleIcon />,
                  },
                  {
                    value: "dock",
                    label: t("common.dockMode"),
                    icon: <DashboardIcon />,
                  },
                  {
                    value: "drawer",
                    label: t("common.drawerMode"),
                    icon: <MenuIcon />,
                  },
                ].map(({ value, label, icon }) => (
                  <MenuItem
                    key={value}
                    onClick={() => {
                      setDisplayMode(value as any);
                      handleLinksMenuClose();
                    }}
                    selected={displayMode === value}
                    sx={{
                      mx: 1,
                      borderRadius: "6px",
                      minHeight: 40,
                      "&.Mui-selected": {
                        backgroundColor: "primary.light",
                        color: "primary.contrastText",
                        "&:hover": {
                          backgroundColor: "primary.main",
                        },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {React.cloneElement(icon, {
                        sx: {
                          fontSize: "1rem",
                          color:
                            displayMode === value
                              ? "primary.contrastText"
                              : "inherit",
                        },
                      })}
                    </ListItemIcon>
                    <ListItemText
                      primaryTypographyProps={{
                        fontSize: "0.875rem",
                        fontWeight: displayMode === value ? 600 : 400,
                      }}
                    >
                      {label}
                    </ListItemText>
                  </MenuItem>
                ))}
              </Menu>

              {/* Dock Position Submenu */}
              <Menu
                anchorEl={dockPositionMenuAnchor}
                open={Boolean(dockPositionMenuAnchor)}
                onClose={handleDockPositionMenuClose}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                PaperProps={{
                  sx: {
                    minWidth: 300,
                    borderRadius: "8px",
                    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
                    border: "1px solid rgba(0, 0, 0, 0.06)",
                    zIndex: (theme) => theme.zIndex.modal + 1,
                  },
                }}
                MenuListProps={{
                  sx: {
                    py: 1,
                  },
                }}
              >
                {[
                  {
                    value: "bottom",
                    label: t("common.bottom"),
                    icon: <VerticalAlignBottomIcon />,
                  },
                  {
                    value: "left",
                    label: t("common.left"),
                    icon: <AlignHorizontalLeftIcon />,
                  },
                  {
                    value: "right",
                    label: t("common.right"),
                    icon: <AlignHorizontalRightIcon />,
                  },
                ].map(({ value, label, icon }) => (
                  <MenuItem
                    key={value}
                    onClick={() => {
                      setDockPosition(value as any);
                      handleDockPositionMenuClose();
                    }}
                    selected={dockPosition === value}
                    sx={{
                      mx: 1,
                      borderRadius: "6px",
                      minHeight: 40,
                      "&.Mui-selected": {
                        backgroundColor: "primary.light",
                        color: "primary.contrastText",
                        "&:hover": {
                          backgroundColor: "primary.main",
                        },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {React.cloneElement(icon, {
                        sx: {
                          fontSize: "1rem",
                          color:
                            dockPosition === value
                              ? "primary.contrastText"
                              : "inherit",
                        },
                      })}
                    </ListItemIcon>
                    <ListItemText
                      primaryTypographyProps={{
                        fontSize: "0.875rem",
                        fontWeight: dockPosition === value ? 600 : 400,
                      }}
                    >
                      {label}
                    </ListItemText>
                  </MenuItem>
                ))}
              </Menu>

              {/* Drawer Position Submenu */}
              <Menu
                anchorEl={drawerPositionMenuAnchor}
                open={Boolean(drawerPositionMenuAnchor)}
                onClose={handleDrawerPositionMenuClose}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                PaperProps={{
                  sx: {
                    minWidth: 300,
                    borderRadius: "8px",
                    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
                    border: "1px solid rgba(0, 0, 0, 0.06)",
                    zIndex: (theme) => theme.zIndex.modal + 1,
                  },
                }}
                MenuListProps={{
                  sx: {
                    py: 1,
                  },
                }}
              >
                {[
                  {
                    value: "bottom",
                    label: t("common.bottom"),
                    icon: <VerticalAlignBottomIcon />,
                  },
                  {
                    value: "left",
                    label: t("common.left"),
                    icon: <AlignHorizontalLeftIcon />,
                  },
                  {
                    value: "right",
                    label: t("common.right"),
                    icon: <AlignHorizontalRightIcon />,
                  },
                ].map(({ value, label, icon }) => (
                  <MenuItem
                    key={value}
                    onClick={() => {
                      setDrawerPosition(value as any);
                      handleDrawerPositionMenuClose();
                    }}
                    selected={drawerPosition === value}
                    sx={{
                      mx: 1,
                      borderRadius: "6px",
                      minHeight: 40,
                      "&.Mui-selected": {
                        backgroundColor: "primary.light",
                        color: "primary.contrastText",
                        "&:hover": {
                          backgroundColor: "primary.main",
                        },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {React.cloneElement(icon, {
                        sx: {
                          fontSize: "1rem",
                          color:
                            drawerPosition === value
                              ? "primary.contrastText"
                              : "inherit",
                        },
                      })}
                    </ListItemIcon>
                    <ListItemText
                      primaryTypographyProps={{
                        fontSize: "0.875rem",
                        fontWeight: drawerPosition === value ? 600 : 400,
                      }}
                    >
                      {label}
                    </ListItemText>
                  </MenuItem>
                ))}
              </Menu>

              {/* User Avatar with Work Status */}
              <Tooltip
                title={
                  workStatus?.currentStatus === "IN"
                    ? workStatus?.isOnBreak
                      ? t("timetracking.status.ON_BREAK")
                      : t("timetracking.working")
                    : t("timetracking.notWorking")
                }
              >
                <IconButton
                  onClick={handleUserMenuOpen}
                  size="small"
                  sx={{
                    ml: 1.5,
                    p: 0,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    variant="dot"
                    sx={{
                      "& .MuiBadge-badge": {
                        backgroundColor:
                          workStatus?.currentStatus === "IN"
                            ? workStatus?.isOnBreak
                              ? "#2196f3" // Blue for on break
                              : "#44b700" // Green for working
                            : "#9e9e9e", // Grey for not working
                        color:
                          workStatus?.currentStatus === "IN"
                            ? workStatus?.isOnBreak
                              ? "#2196f3" // Blue for on break
                              : "#44b700" // Green for working
                            : "#9e9e9e", // Grey for not working
                        boxShadow: (theme) =>
                          workStatus?.currentStatus === "IN"
                            ? workStatus?.isOnBreak
                              ? `0 0 0 3px ${theme.palette.background.paper}, 0 0 8px rgba(33, 150, 243, 0.6)` // Blue glow for break
                              : `0 0 0 3px ${theme.palette.background.paper}, 0 0 8px rgba(68, 183, 0, 0.6)` // Green glow for working
                            : `0 0 0 2px ${theme.palette.background.paper}`,
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        "&::after": {
                          position: "absolute",
                          top: -2,
                          left: -2,
                          width: "calc(100% + 4px)",
                          height: "calc(100% + 4px)",
                          borderRadius: "50%",
                          animation:
                            workStatus?.currentStatus === "IN"
                              ? "ripple 1.5s infinite ease-in-out"
                              : "none",
                          border: "2px solid currentColor",
                          content: '""',
                        },
                        "@keyframes ripple": {
                          "0%": {
                            transform: "scale(0.6)",
                            opacity: 1,
                          },
                          "50%": {
                            transform: "scale(1.2)",
                            opacity: 0.5,
                          },
                          "100%": {
                            transform: "scale(2.0)",
                            opacity: 0,
                          },
                        },
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: "primary.main",
                        fontSize: "1rem",
                        fontWeight: 600,
                        border: (theme) =>
                          `2px solid ${
                            workStatus?.currentStatus === "IN"
                              ? workStatus?.isOnBreak
                                ? theme.palette.info.main // Blue border for break
                                : theme.palette.success.main // Green border for working
                              : theme.palette.grey[400]
                          }`,
                      }}
                    >
                      {getInitials()}
                    </Avatar>
                  </Badge>
                </IconButton>
              </Tooltip>

              {/* User Menu */}
              <Menu
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={handleUserMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    minWidth: 320,
                    borderRadius: 2,
                    boxShadow: (theme) =>
                      isDarkMode
                        ? "0 8px 32px rgba(0,0,0,0.5)"
                        : "0 8px 32px rgba(0,0,0,0.15)",
                    overflow: "visible",
                    "&::before": {
                      content: '""',
                      display: "block",
                      position: "absolute",
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: "background.paper",
                      transform: "translateY(-50%) rotate(45deg)",
                      zIndex: 0,
                    },
                  },
                }}
              >
                {/* User Info Header */}
                <Box
                  sx={{
                    px: 2.5,
                    py: 2,
                    background: (theme) =>
                      `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: "primary.main",
                        fontSize: "1.2rem",
                        fontWeight: 700,
                      }}
                    >
                      {getInitials()}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {displayName}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {displayEmail}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                <Divider />

                {/* Work Status Section */}
                {hasTimeTrackingAccess && (
                  <>
                    <Box sx={{ px: 2.5, py: 2 }}>
                      <Stack spacing={1.5}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              color: "text.secondary",
                              textTransform: "uppercase",
                              fontSize: "0.7rem",
                              letterSpacing: 0.5,
                            }}
                          >
                            {t("timetracking.workStatus")}
                          </Typography>
                          <Chip
                            icon={
                              workStatus?.currentStatus === "IN" ? (
                                workStatus?.isOnBreak ? (
                                  <PauseCircleIcon />
                                ) : (
                                  <CheckCircleIcon />
                                )
                              ) : (
                                <CancelIcon />
                              )
                            }
                            label={
                              workStatus?.currentStatus === "IN"
                                ? workStatus?.isOnBreak
                                  ? t("timetracking.status.ON_BREAK")
                                  : t("timetracking.working")
                                : t("timetracking.notWorking")
                            }
                            size="small"
                            color={
                              workStatus?.currentStatus === "IN"
                                ? workStatus?.isOnBreak
                                  ? "info"
                                  : "success"
                                : "default"
                            }
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.75rem",
                            }}
                          />
                        </Stack>

                        {workStatus?.currentStatus === "IN" && (
                          <Paper
                            elevation={0}
                            sx={{
                              p: 1.5,
                              bgcolor: (theme) =>
                                workStatus?.isOnBreak
                                  ? alpha(theme.palette.info.main, 0.1)
                                  : alpha(theme.palette.success.main, 0.1),
                              borderRadius: 1.5,
                              border: (theme) =>
                                workStatus?.isOnBreak
                                  ? `1px solid ${alpha(theme.palette.info.main, 0.2)}`
                                  : `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                            }}
                          >
                            <Stack spacing={1}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                              >
                                <TimerIcon
                                  fontSize="small"
                                  sx={{ color: "success.main" }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {workStatus.currentSessionDuration}
                                </Typography>
                              </Stack>
                              <Typography
                                variant="caption"
                                sx={{ color: "text.secondary" }}
                              >
                                {t("timetracking.workingDuration")}
                              </Typography>
                            </Stack>
                          </Paper>
                        )}

                        {workStatus?.totalHoursWorked !== undefined && (
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{
                              px: 1.5,
                              py: 1,
                              bgcolor: (theme) =>
                                alpha(theme.palette.info.main, 0.08),
                              borderRadius: 1,
                            }}
                          >
                            <Stack direction="row" alignItems="center" gap={1}>
                              <TrendingUpIcon
                                fontSize="small"
                                sx={{ color: "info.main" }}
                              />
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 500 }}
                              >
                                {t("timetracking.todayTotal")}
                              </Typography>
                            </Stack>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, color: "info.main" }}
                            >
                              {workStatus.totalHoursWorked.toFixed(2)}h
                            </Typography>
                          </Stack>
                        )}

                        <MenuItem
                          onClick={handleNavigateToTimeTracking}
                          sx={{
                            borderRadius: 1,
                            py: 1,
                            px: 1.5,
                            bgcolor: (theme) =>
                              alpha(theme.palette.primary.main, 0.08),
                            "&:hover": {
                              bgcolor: (theme) =>
                                alpha(theme.palette.primary.main, 0.15),
                            },
                          }}
                        >
                          <ListItemIcon>
                            <AccessTimeIcon
                              fontSize="small"
                              sx={{ color: "primary.main" }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={t("timetracking.goToTimeTracking")}
                            primaryTypographyProps={{
                              fontSize: "0.9rem",
                              fontWeight: 600,
                              color: "primary.main",
                            }}
                          />
                        </MenuItem>
                      </Stack>
                    </Box>
                    <Divider />
                  </>
                )}

                {/* Logout */}
                <MenuItem
                  onClick={handleLogout}
                  sx={{
                    py: 1.5,
                    px: 2.5,
                    color: "error.main",
                    "&:hover": {
                      bgcolor: (theme) => alpha(theme.palette.error.main, 0.1),
                    },
                  }}
                >
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" sx={{ color: "error.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("common.logout")}
                    primaryTypographyProps={{
                      fontSize: "0.95rem",
                      fontWeight: 600,
                    }}
                  />
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
